/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { disposeObject3D, disposeMaterial, boundsFromBox, reconcileSceneBounds } from './sceneUtils.js';

export function fitUtmToLocalTransform(collars, project) {
  if (!project) return null;
  const pts = [];
  let minE = Infinity, minN = Infinity, maxE = -Infinity, maxN = -Infinity;
  const longitudes = (collars || []).map((collar) => Number(collar?.lng)).filter(Number.isFinite);
  const meanLongitude = longitudes.length
    ? longitudes.reduce((sum, longitude) => sum + longitude, 0) / longitudes.length
    : null;
  const utmZone = Number.isFinite(meanLongitude)
    ? Math.floor((meanLongitude + 180) / 6) + 1
    : null;
  (collars || []).forEach((c) => {
    let e = Number(c?.easting);
    let n = Number(c?.northing);
    if ((!Number.isFinite(e) || !Number.isFinite(n)) && Number.isFinite(utmZone)) {
      const projected = latLngToUtm(c.lat, c.lng, utmZone);
      e = projected?.easting;
      n = projected?.northing;
    }
    if (!Number.isFinite(e) || !Number.isFinite(n)) return;
    const local = project(c.lat, c.lng);
    if (!Number.isFinite(local?.x) || !Number.isFinite(local?.y)) return;
    pts.push({ e, n, x: local.x, y: local.y });
    if (e < minE) minE = e;
    if (e > maxE) maxE = e;
    if (n < minN) minN = n;
    if (n > maxN) maxN = n;
  });
  if (pts.length < 2) return null;

  const count = pts.length;
  let me = 0, mn = 0, mx = 0, my = 0;
  pts.forEach((p) => { me += p.e; mn += p.n; mx += p.x; my += p.y; });
  me /= count; mn /= count; mx /= count; my /= count;

  let sden = 0, sa = 0, sb = 0;
  pts.forEach((p) => {
    const e = p.e - me, n = p.n - mn, x = p.x - mx, y = p.y - my;
    sden += e * e + n * n;
    sa += e * x + n * y;
    sb += e * y - n * x;
  });
  if (sden === 0) return null;
  const a = sa / sden;
  const b = sb / sden;
  const tx = mx - (a * me - b * mn);
  const ty = my - (b * me + a * mn);

  return {
    apply: (e, n) => ({ x: a * e - b * n + tx, y: b * e + a * n + ty }),
    projectLatLng: (latitude, longitude) => {
      const projected = latLngToUtm(latitude, longitude, utmZone);
      return projected ? { x: a * projected.easting - b * projected.northing + tx, y: b * projected.easting + a * projected.northing + ty } : null;
    },
    zone: utmZone,
    bbox: { minE, minN, maxE, maxN },
  };
}

export function latLngToUtm(latitude, longitude, zone) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zone)) return null;
  const radians = Math.PI / 180;
  const phi = lat * radians;
  const lambda = lng * radians;
  const centralMeridian = ((zone - 1) * 6 - 180 + 3) * radians;
  const semiMajor = 6378137;
  const eccentricitySquared = 0.0066943799901413165;
  const eccentricityPrimeSquared = eccentricitySquared / (1 - eccentricitySquared);
  const scale = 0.9996;
  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);
  const tanPhi = Math.tan(phi);
  const radius = semiMajor / Math.sqrt(1 - eccentricitySquared * sinPhi * sinPhi);
  const tangent = tanPhi * tanPhi;
  const curvature = eccentricityPrimeSquared * cosPhi * cosPhi;
  const deltaLambda = cosPhi * (lambda - centralMeridian);
  const meridional = semiMajor * (
    (1 - eccentricitySquared / 4 - 3 * eccentricitySquared ** 2 / 64 - 5 * eccentricitySquared ** 3 / 256) * phi
      - (3 * eccentricitySquared / 8 + 3 * eccentricitySquared ** 2 / 32 + 45 * eccentricitySquared ** 3 / 1024) * Math.sin(2 * phi)
      + (15 * eccentricitySquared ** 2 / 256 + 45 * eccentricitySquared ** 3 / 1024) * Math.sin(4 * phi)
      - (35 * eccentricitySquared ** 3 / 3072) * Math.sin(6 * phi)
  );
  const easting = scale * radius * (
    deltaLambda + (1 - tangent + curvature) * deltaLambda ** 3 / 6
      + (5 - 18 * tangent + tangent ** 2 + 72 * curvature - 58 * eccentricityPrimeSquared) * deltaLambda ** 5 / 120
  ) + 500000;
  let northing = scale * (
    meridional + radius * tanPhi * (
      deltaLambda ** 2 / 2
      + (5 - tangent + 9 * curvature + 4 * curvature ** 2) * deltaLambda ** 4 / 24
      + (61 - 58 * tangent + tangent ** 2 + 600 * curvature - 330 * eccentricityPrimeSquared) * deltaLambda ** 6 / 720
    )
  );
  if (lat < 0) northing += 10000000;
  return { easting, northing };
}

export function isInProjectedRange(x, y, transform, margin = 50_000) {
  const { minE, minN, maxE, maxN } = transform.bbox;
  return x >= minE - margin && x <= maxE + margin && y >= minN - margin && y <= maxN + margin;
}

export function toLocalFrameHole(hole, transform) {
  if (!transform) return hole;
  const sample = (hole.points || []).find((p) => Number.isFinite(p?.x) && Number.isFinite(p?.y));
  if (!sample || !isInProjectedRange(sample.x, sample.y, transform)) return hole;
  const points = (hole.points || []).map((p) => {
    const { x, y } = transform.apply(p.x, p.y);
    return { ...p, x, y };
  });
  return { ...hole, points };
}

export function applyGeoreferenceIfInRange(group, transform) {
  if (!transform) return false;
  const box = new THREE.Box3().setFromObject(group);
  if (box.isEmpty()) return false;
  const cx = (box.min.x + box.max.x) / 2;
  const cy = (box.min.y + box.max.y) / 2;
  if (!isInProjectedRange(cx, cy, transform)) return false;
  group.traverse((child) => {
    const pos = child.isMesh ? child.geometry?.attributes?.position : null;
    if (!pos) return;
    for (let i = 0; i < pos.count; i += 1) {
      const { x, y } = transform.apply(pos.getX(i), pos.getY(i));
      pos.setXYZ(i, x, y, pos.getZ(i));
    }
    pos.needsUpdate = true;
    child.geometry.computeBoundingBox();
    child.geometry.computeBoundingSphere();
  });
  return true;
}

export function prepareObjMeshGroup(group, name) {
  const material = new THREE.MeshStandardMaterial({
    color: 0xf2c94c,
    roughness: 0.62,
    metalness: 0.02,
    transparent: true,
    opacity: 0.52,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  let meshCount = 0;
  let vertexCount = 0;
  group.name = name || 'OBJ mesh';
  group.userData = { type: 'obj-mesh', name: group.name };
  group.traverse((child) => {
    if (!child.isMesh) return;
    meshCount += 1;
    const geometry = child.geometry;
    if (geometry?.attributes?.position) vertexCount += geometry.attributes.position.count;
    geometry?.computeVertexNormals?.();
    disposeMaterial(child.material);
    child.material = material.clone();
    child.castShadow = false;
    child.receiveShadow = false;
    child.userData = { type: 'obj-mesh', name: group.name };
  });
  material.dispose();
  const box = new THREE.Box3().setFromObject(group);
  const bounds = box.isEmpty() ? null : boundsFromBox(box);
  return { meshCount, vertexCount, bounds };
}
